import { checkDocumentContentHash } from '@sib/api';
import { FileItem } from '@sib/types';
import {
  createConcurrencyPool,
  runWithConcurrencyPool,
} from './concurrency-pool';
import { resolveDocumentUploadPoolSize } from './document-upload-concurrency';
import { CHUNK_SIZE, computeFileMd5, encodeMd5HashAsBase64 } from './utils';

type ResolveFileItem = (fileId: string) => FileItem | undefined;

const DEFAULT_DUPLICATE_CONTENT_MESSAGE =
  'A file with identical content already exists';

const IN_BATCH_DUPLICATE_CONTENT_MESSAGE =
  'Another file in this batch has identical content';

export type UploadDocumentWithMd5 = {
  md5Hash: string;
};

export type DocumentUploadFileWithMd5 = {
  fileItem: FileItem;
  md5Hash: string;
};

type DbDuplicateCheckResult = {
  isDuplicate: boolean;
  message?: string;
};

export type DocumentUploadValidationContext = {
  seenHashesInBatch: Set<string>;
  dbDuplicateByHash: Map<string, DbDuplicateCheckResult>;
};

type ValidateDocumentUploadByMd5Params = {
  organizationId: string;
  fileItems: FileItem[];
  resolveFileItem?: ResolveFileItem;
  onDuplicate?: (fileItem: FileItem, error: string) => void;
  onMd5Error?: (fileItem: FileItem, error: unknown) => void;
};

type ValidateDocumentUploadFileWithMd5Params = {
  fileItem: FileItem;
  md5Hash: string;
  context: DocumentUploadValidationContext;
  onDuplicate?: (fileItem: FileItem, error: string) => void;
};

const md5HashPromises = new Map<string, Promise<string>>();

const documentMd5Pool = createConcurrencyPool(() =>
  resolveDocumentUploadPoolSize('md5')
);

export const createDocumentUploadValidationContext =
  (): DocumentUploadValidationContext => ({
    seenHashesInBatch: new Set<string>(),
    dbDuplicateByHash: new Map<string, DbDuplicateCheckResult>(),
  });

export const computeDocumentUploadMd5Hash = async (
  fileItem: FileItem
): Promise<string> => {
  const md5HashBytes = await computeFileMd5(fileItem.file, CHUNK_SIZE);
  const md5Hash = encodeMd5HashAsBase64(md5HashBytes);

  return md5Hash;
};

export const clearDocumentMd5HashPrefetch = (fileId: string) => {
  md5HashPromises.delete(fileId);
};

export const syncDocumentMd5HashPrefetch = (fileIds: readonly string[]) => {
  const activeFileIds = new Set(fileIds);

  [...md5HashPromises.keys()].forEach(fileId => {
    if (!activeFileIds.has(fileId)) clearDocumentMd5HashPrefetch(fileId);
  });
};

export const prefetchDocumentMd5Hash = (
  fileItem: FileItem,
  onReady?: (fileItem: FileItem, md5Hash: string) => void,
  onError?: (fileItem: FileItem, error: unknown) => void
): Promise<string> => {
  if (fileItem.md5Hash) return Promise.resolve(fileItem.md5Hash);

  const existingPromise = md5HashPromises.get(fileItem.id);
  if (existingPromise) return existingPromise;

  const promise = documentMd5Pool
    .run(() => computeDocumentUploadMd5Hash(fileItem))
    .then(md5Hash => {
      onReady?.(fileItem, md5Hash);

      return md5Hash;
    })
    .catch(error => {
      clearDocumentMd5HashPrefetch(fileItem.id);
      onError?.(fileItem, error);

      throw error;
    });

  md5HashPromises.set(fileItem.id, promise);

  return promise;
};

export const waitForDocumentMd5Hash = (
  fileItem: FileItem,
  resolveFileItem?: ResolveFileItem
): Promise<string> => {
  const existingPromise = md5HashPromises.get(fileItem.id);
  if (existingPromise) return existingPromise;

  const latestFileItem = resolveFileItem?.(fileItem.id) ?? fileItem;

  if (latestFileItem.md5Hash) return Promise.resolve(latestFileItem.md5Hash);

  return prefetchDocumentMd5Hash(latestFileItem);
};

export const waitForDocumentMd5Hashes = async (
  fileItems: FileItem[],
  resolveFileItem?: ResolveFileItem,
  onMd5Error?: (fileItem: FileItem, error: unknown) => void
): Promise<DocumentUploadFileWithMd5[]> => {
  const results = await runWithConcurrencyPool(
    fileItems,
    () => resolveDocumentUploadPoolSize('md5'),
    async fileItem => {
      const latestFileItem = resolveFileItem?.(fileItem.id) ?? fileItem;

      try {
        const md5Hash = await waitForDocumentMd5Hash(
          latestFileItem,
          resolveFileItem
        );

        return {
          fileItem: latestFileItem,
          md5Hash,
        };
      } catch (error) {
        onMd5Error?.(latestFileItem, error);

        return null;
      }
    }
  );

  return results.filter(
    (entry): entry is DocumentUploadFileWithMd5 => entry !== null
  );
};

const prefetchDbDuplicateChecks = async (
  organizationId: string,
  md5Hashes: string[],
  context: DocumentUploadValidationContext
): Promise<void> => {
  const hashesToCheck = [...new Set(md5Hashes)].filter(
    hash => !context.dbDuplicateByHash.has(hash)
  );

  if (hashesToCheck.length === 0) return;

  const { results } = await checkDocumentContentHash(organizationId, {
    md5Hashes: hashesToCheck,
  });

  for (const result of results)
    context.dbDuplicateByHash.set(result.md5Hash, {
      isDuplicate: result.isDuplicate,
      message: result.message,
    });
};

const getDbDuplicateCheckResult = (
  md5Hash: string,
  context: DocumentUploadValidationContext
): DbDuplicateCheckResult =>
  context.dbDuplicateByHash.get(md5Hash) ?? { isDuplicate: false };

const validateDocumentUploadFileWithMd5 = async ({
  fileItem,
  md5Hash,
  context,
  onDuplicate,
}: ValidateDocumentUploadFileWithMd5Params): Promise<DocumentUploadFileWithMd5 | null> => {
  const { isDuplicate, message } = getDbDuplicateCheckResult(md5Hash, context);

  if (isDuplicate) {
    onDuplicate?.(fileItem, message ?? DEFAULT_DUPLICATE_CONTENT_MESSAGE);

    return null;
  }

  if (context.seenHashesInBatch.has(md5Hash)) {
    onDuplicate?.(fileItem, IN_BATCH_DUPLICATE_CONTENT_MESSAGE);

    return null;
  }

  context.seenHashesInBatch.add(md5Hash);

  return { fileItem, md5Hash };
};

type ValidateDocumentUploadFileByMd5Params = {
  organizationId: string;
  fileItem: FileItem;
  context: DocumentUploadValidationContext;
  resolveFileItem?: ResolveFileItem;
  onDuplicate?: (fileItem: FileItem, error: string) => void;
};

export const validateDocumentUploadFileByMd5 = async ({
  organizationId,
  fileItem,
  context,
  resolveFileItem,
  onDuplicate,
}: ValidateDocumentUploadFileByMd5Params): Promise<DocumentUploadFileWithMd5 | null> => {
  const latestFileItem = resolveFileItem?.(fileItem.id) ?? fileItem;
  const md5Hash = await waitForDocumentMd5Hash(latestFileItem, resolveFileItem);

  await prefetchDbDuplicateChecks(organizationId, [md5Hash], context);

  return validateDocumentUploadFileWithMd5({
    fileItem: latestFileItem,
    md5Hash,
    context,
    onDuplicate,
  });
};

export const validateDocumentUploadByMd5 = async ({
  organizationId,
  fileItems,
  resolveFileItem,
  onDuplicate,
  onMd5Error,
}: ValidateDocumentUploadByMd5Params): Promise<DocumentUploadFileWithMd5[]> => {
  const filesWithMd5 = await waitForDocumentMd5Hashes(
    fileItems,
    resolveFileItem,
    onMd5Error
  );
  const context = createDocumentUploadValidationContext();

  await prefetchDbDuplicateChecks(
    organizationId,
    filesWithMd5.map(({ md5Hash }) => md5Hash),
    context
  );
  const validatedFiles: DocumentUploadFileWithMd5[] = [];

  for (const { fileItem, md5Hash } of filesWithMd5) {
    const validatedFile = await validateDocumentUploadFileWithMd5({
      fileItem,
      md5Hash,
      context,
      onDuplicate,
    });

    if (validatedFile) validatedFiles.push(validatedFile);
  }

  return validatedFiles;
};
